"use client";

import { useCallback, useMemo, useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboardOrThrow } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";

const WHITESPACE_RE = /\s+/;

function countWords(text: string): number {
  return text
    .trim()
    .split(WHITESPACE_RE)
    .filter((w) => w.length > 0).length;
}

interface Section {
  id: string;
  label: string;
  prompt: string;
  placeholder: string;
  tip: string;
}

const SECTION_IDS = ["background", "methods", "results", "conclusion"] as const;

/** English strings. A translated content file overrides these through `ui`. */
export const ABSTRACT_GENERATOR_STRINGS = {
  targetWordCount: "Target Word Count",
  words: "words",
  progress: "Progress",
  progressLine:
    "one:{total} / {target} word ({percent}%)|other:{total} / {target} words ({percent}%)",
  sectionWords: "one:{count} word|other:{count} words",
  abstractPreview: "Abstract Preview",
  copied: "Copied!",
  copy: "Copy",
  wordsTotalCount: "one:{count} word total|other:{count} words total",
  tipsForWritingAStrong: "Tips for Writing a Strong Abstract",
  writeItLast: "Write it last",
  finishYourPaperFirstThen: " — Finish your paper first, then summarize it.",
  beConcise: "Be concise",
  mostJournalsRequire150300:
    " — Most journals require 150-300 words. Every word should count.",
  avoidJargon: "Avoid jargon",
  useClearLanguageAccessibleTo:
    " — Use clear language accessible to a broad audience.",
  includeKeywords: "Include keywords",
  useTermsThatResearchersWould:
    " — Use terms that researchers would search for.",
  doNotCiteReferences: "Do not cite references",
  abstractsTypicallyStandAloneWithout:
    " — Abstracts typically stand alone without citations.",
  usePastTense: "Use past tense",
  forMethodsAndResultsPresent:
    " — For methods and results; present tense for conclusions and implications.",
  backgroundLabel: "Background / Purpose",
  backgroundPrompt: "What problem does your research address?",
  backgroundPlaceholder: "This study investigates the relationship between...",
  backgroundTip:
    "State the research problem, its significance, and your research question or hypothesis. Keep it to 1-2 sentences.",
  methodsLabel: "Methods",
  methodsPrompt: "What methods did you use?",
  methodsPlaceholder: "A mixed-methods approach was employed, including...",
  methodsTip:
    "Briefly describe your research design, participants, data collection methods, and analysis approach.",
  resultsLabel: "Results",
  resultsPrompt: "What did you find?",
  resultsPlaceholder: "The results indicate that...",
  resultsTip:
    "Summarize your key findings with specific data points or trends. Focus on results that answer your research question.",
  conclusionLabel: "Discussion / Conclusion",
  conclusionPrompt: "What do your findings mean?",
  conclusionPlaceholder: "These findings suggest that...",
  conclusionTip:
    "Interpret your results, state their implications, and mention any limitations or future research directions.",
} as const;

type Strings = typeof ABSTRACT_GENERATOR_STRINGS;

function buildSections(strings: Strings): Section[] {
  return [
    {
      id: "background",
      label: strings.backgroundLabel,
      prompt: strings.backgroundPrompt,
      placeholder: strings.backgroundPlaceholder,
      tip: strings.backgroundTip,
    },
    {
      id: "methods",
      label: strings.methodsLabel,
      prompt: strings.methodsPrompt,
      placeholder: strings.methodsPlaceholder,
      tip: strings.methodsTip,
    },
    {
      id: "results",
      label: strings.resultsLabel,
      prompt: strings.resultsPrompt,
      placeholder: strings.resultsPlaceholder,
      tip: strings.resultsTip,
    },
    {
      id: "conclusion",
      label: strings.conclusionLabel,
      prompt: strings.conclusionPrompt,
      placeholder: strings.conclusionPlaceholder,
      tip: strings.conclusionTip,
    },
  ];
}

export default function AbstractGeneratorWidget() {
  const [sections, setSections] = useState<Record<string, string>>({
    background: "",
    methods: "",
    results: "",
    conclusion: "",
  });
  const [targetWords, setTargetWords] = useState(250);
  const [copied, setCopied] = useState(false);

  const sectionWordCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const key of Object.keys(sections)) {
      counts[key] = countWords(sections[key]);
    }
    return counts;
  }, [sections]);

  const totalWords = useMemo(() => {
    return Object.values(sectionWordCounts).reduce((sum, c) => sum + c, 0);
  }, [sectionWordCounts]);

  const progressPercent = useMemo(() => {
    if (targetWords <= 0) {
      return 0;
    }
    return Math.min(Math.round((totalWords / targetWords) * 100), 100);
  }, [totalWords, targetWords]);

  const combinedAbstract = useMemo(() => {
    const parts: string[] = [];
    for (const id of SECTION_IDS) {
      const text = sections[id]?.trim();
      if (text) {
        parts.push(text);
      }
    }
    return parts.join(" ");
  }, [sections]);

  const handleSectionChange = useCallback((id: string, value: string) => {
    setSections((prev) => ({ ...prev, [id]: value }));
  }, []);

  const handleCopy = useCallback(async () => {
    if (!combinedAbstract) {
      return;
    }
    try {
      await writeToClipboardOrThrow(combinedAbstract);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard API might not be available
    }
  }, [combinedAbstract]);

  const progressColor = useMemo(() => {
    if (totalWords === 0) {
      return "bg-muted";
    }
    if (totalWords <= targetWords) {
      return "bg-primary";
    }
    return "bg-amber-500";
  }, [totalWords, targetWords]);

  const wordCountColor = useMemo(() => {
    if (totalWords === 0) {
      return "text-muted-foreground";
    }
    if (totalWords >= targetWords - 25 && totalWords <= targetWords + 25) {
      return "text-green-600 dark:text-green-400";
    }
    if (totalWords > targetWords + 25) {
      return "text-amber-600 dark:text-amber-400";
    }
    return "text-muted-foreground";
  }, [totalWords, targetWords]);

  const strings = useToolUi(ABSTRACT_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const sectionList = useMemo(() => buildSections(strings), [strings]);

  return (
    <>
      {/* Target word count */}
      <section className="space-y-3">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="target-words"
        >
          {strings.targetWordCount}
        </label>
        <div className="flex items-center gap-3">
          <input
            className="w-28 rounded-lg border border-border bg-card px-4 py-2.5 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="target-words"
            min={50}
            onChange={(e) => {
              const val = parseIntegerInput(e.target.value);
              if (val !== null && val > 0) {
                setTargetWords(val);
              }
            }}
            type="number"
            value={targetWords}
          />
          <span className="text-muted-foreground text-sm">{strings.words}</span>
        </div>
      </section>
      {/* Progress bar */}
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-foreground text-sm">{strings.progress}</span>
          <span className={`font-medium text-sm ${wordCountColor}`}>
            {tPlural(strings.progressLine, locale, targetWords, {
              total: totalWords,
              target: targetWords,
              percent: progressPercent,
            })}
          </span>
        </div>
        <div className="h-3 overflow-hidden rounded-full bg-muted">
          <div
            className={`h-full rounded-full transition-all duration-300 ${progressColor}`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </section>
      {/* Sections */}
      <section className="space-y-8">
        {sectionList.map((sec) => (
          <div className="space-y-2" key={sec.id}>
            <label
              className="font-medium text-foreground text-sm"
              htmlFor={`section-${sec.id}`}
            >
              {sec.label}
            </label>
            <p className="text-muted-foreground text-sm">{sec.prompt}</p>
            <textarea
              className="min-h-[100px] w-full rounded-lg border border-border bg-card px-4 py-3 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              id={`section-${sec.id}`}
              onChange={(e) => handleSectionChange(sec.id, e.target.value)}
              placeholder={sec.placeholder}
              value={sections[sec.id]}
            />
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-xs italic">{sec.tip}</p>
              <p className="shrink-0 text-muted-foreground text-xs">
                {tPlural(
                  strings.sectionWords,
                  locale,
                  sectionWordCounts[sec.id]
                )}
              </p>
            </div>
          </div>
        ))}
      </section>
      {/* Preview */}
      {combinedAbstract && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-foreground text-sm">
              {strings.abstractPreview}
            </h2>
            <button
              className="rounded-md border border-border bg-card px-3 py-1.5 text-foreground text-xs transition-colors hover:bg-accent"
              onClick={handleCopy}
              type="button"
            >
              {copied ? strings.copied : strings.copy}
            </button>
          </div>
          <div className="rounded-lg border border-border bg-card p-6">
            <p className="text-foreground text-sm leading-relaxed">
              {combinedAbstract}
            </p>
          </div>
          <p className="text-muted-foreground text-xs">
            {tPlural(strings.wordsTotalCount, locale, totalWords)}
          </p>
        </section>
      )}
      {/* Tips */}
      <section className="rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="mb-4 font-semibold text-foreground text-sm">
          {strings.tipsForWritingAStrong}
        </h2>
        <ul className="space-y-2 text-muted-foreground text-sm">
          <li>
            <strong>{strings.writeItLast}</strong>
            {strings.finishYourPaperFirstThen}
          </li>
          <li>
            <strong>{strings.beConcise}</strong>
            {strings.mostJournalsRequire150300}
          </li>
          <li>
            <strong>{strings.avoidJargon}</strong>
            {strings.useClearLanguageAccessibleTo}
          </li>
          <li>
            <strong>{strings.includeKeywords}</strong>
            {strings.useTermsThatResearchersWould}
          </li>
          <li>
            <strong>{strings.doNotCiteReferences}</strong>
            {strings.abstractsTypicallyStandAloneWithout}
          </li>
          <li>
            <strong>{strings.usePastTense}</strong>
            {strings.forMethodsAndResultsPresent}
          </li>
        </ul>
      </section>
      {/* Related tools */}
    </>
  );
}
